import { useEffect, useState, useCallback, useMemo } from 'react'
import { apiGet, apiPatch } from '../api'
import type { SessionRow } from '../api'

function relTime(ms: number): string {
  const diff = Date.now() - ms
  if (diff < 60_000) return 'just now'
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

// The agent ingests every recent claude+codex session, which can run to 30+
// rows on a busy day. Cap the visible list and let the user expand on demand.
const DEFAULT_VISIBLE = 5
const STALE_HOURS = 24

export default function ActiveSessions() {
  const [sessions, setSessions] = useState<SessionRow[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [hideStale, setHideStale] = useState(true)

  const load = useCallback(async () => {
    try {
      const res = await apiGet<{ sessions: SessionRow[] }>('/sessions')
      setSessions(res.sessions)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
  }, [load])

  const dismiss = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setSessions(prev => prev.filter(s => s.id !== id))
    try { await apiPatch(`/sessions/${id}`, { dismissed: true }) } catch { load() }
  }

  // Bucket: fresh (<24h) vs stale. Sort by most-recent first within each.
  const { fresh, stale } = useMemo(() => {
    const cutoff = Date.now() - STALE_HOURS * 3600_000
    const sorted = [...sessions].sort((a, b) => b.last_activity - a.last_activity)
    const f: SessionRow[] = []
    const s: SessionRow[] = []
    for (const x of sorted) (x.last_activity >= cutoff ? f : s).push(x)
    return { fresh: f, stale: s }
  }, [sessions])

  const list = hideStale ? fresh : [...fresh, ...stale]
  const visible = showAll ? list : list.slice(0, DEFAULT_VISIBLE)
  const overflow = list.length - visible.length

  return (
    <section className="panel work-panel">
      <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span>Active sessions</span>
        <span style={{ display: 'flex', gap: 6, alignItems: 'baseline', fontSize: '0.7rem' }}>
          <span className="mono" style={{ color: 'var(--text-muted)' }}>
            {fresh.length} fresh{stale.length ? ` · ${stale.length} stale` : ''}
          </span>
          {stale.length > 0 && (
            <button type="button" onClick={() => setHideStale(v => !v)}
              style={{ minHeight: 24, padding: '0.1rem 0.4rem' }}>
              {hideStale ? `+ stale` : `hide stale`}
            </button>
          )}
        </span>
      </div>
      {list.length === 0 && (
        <div className="empty-state">
          No active sessions{hideStale && stale.length > 0 ? ' in the last 24h.' : '.'}
        </div>
      )}
      <div
        className="session-list"
        style={{
          maxHeight: showAll ? '60vh' : 'unset',
          overflowY: showAll ? 'auto' : 'visible',
        }}
      >
        {visible.map(s => {
          const isOpen = expanded === s.id
          return (
            <div key={s.id} className="row" onClick={() => setExpanded(isOpen ? null : s.id)} style={{ cursor: 'pointer' }}>
              <span className="mono pill">{s.project_label}</span>
              <span className={`pill ${s.tool === 'claude_code' ? 'amber' : 'warm'}`}>{s.tool === 'claude_code' ? 'claude' : 'codex'}</span>
              <span className="grow">
                <span className={isOpen ? '' : 'truncate'} style={{ display: 'block' }}>
                  {s.current_summary ?? <span className="muted italic">(no summary)</span>}
                </span>
              </span>
              <span className="muted mono" style={{ flex: '0 0 auto' }}>{relTime(s.last_activity)}</span>
              <button
                type="button"
                onClick={e => dismiss(s.id, e)}
                aria-label="Dismiss session"
                title="Dismiss until next activity"
                style={{ minHeight: 28, padding: '0.1rem 0.5rem', fontSize: '0.85rem', border: '1px solid transparent', color: 'var(--text-muted)' }}
              >
                ×
              </button>
            </div>
          )
        })}
      </div>
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          style={{
            marginTop: 6, width: '100%', minHeight: 30,
            background: 'transparent', border: '1px dashed var(--text-muted, #6b7795)',
            color: 'var(--text-muted, #6b7795)', fontSize: '0.75rem',
            cursor: 'pointer', borderRadius: 6,
          }}
        >
          show {overflow} more (scrollable)
        </button>
      )}
      {showAll && list.length > DEFAULT_VISIBLE && (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          style={{
            marginTop: 6, width: '100%', minHeight: 28,
            background: 'transparent', border: 'none',
            color: 'var(--text-muted, #6b7795)', fontSize: '0.7rem',
            cursor: 'pointer',
          }}
        >
          collapse
        </button>
      )}
    </section>
  )
}
